/**
 * The tenant's sites (PLAN §3, §9.1): the automatic cloud site and the
 * on-prem ones, with their studios; open one (its nodes), create one, rename
 * or delete an on-prem site. `sites`, `loading` and `error` are independent.
 */
import type { SiteWire } from '@acp/api-contract/sites.js';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { isPlainClick } from '../../lib/links.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type SitesTableProps = {
  readonly sites: readonly SiteWire[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly onCreate: () => void;
  readonly onRetry: () => void;
  readonly onOpen: (siteId: string) => void;
  readonly onRename: (siteId: string) => void;
  readonly onDelete: (siteId: string) => void;
};

export function siteHref(siteId: string): string {
  return `/admin/sites/${encodeURIComponent(siteId)}`;
}

export function SitesTable({ sites, loading, error, onCreate, onRetry, onOpen, onRename, onDelete }: SitesTableProps) {
  return (
    <Card data-testid="sites-table">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Sites</CardTitle>
          <CardDescription>Where your servers run. Each on-prem site holds a main and a backup node, and optionally a LAN witness.</CardDescription>
        </div>
        <Button onClick={onCreate} data-testid="create-site">
          <PlusIcon />
          New site
        </Button>
      </CardHeader>
      <CardContent>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load sites: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && sites.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading sites…</p> : null}
        {sites.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Studios</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.map((s) => (
                <TableRow key={s.siteId} data-testid="site-row">
                  <TableCell className="font-medium">
                    <a
                      href={siteHref(s.siteId)}
                      className="hover:underline"
                      data-testid="open-site"
                      onClick={(event) => {
                        if (!isPlainClick(event)) return;
                        event.preventDefault();
                        onOpen(s.siteId);
                      }}
                    >
                      {s.name}
                    </a>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{s.siteId}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={s.kind === 'cloud' ? 'secondary' : 'outline'}>{s.kind === 'cloud' ? 'cloud' : 'on-prem'}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{s.studios.length === 0 ? '–' : s.studios.join(', ')}</TableCell>
                  <TableCell>
                    {s.kind === 'onprem' ? (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => onRename(s.siteId)} data-testid="rename-site">
                          Rename
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(s.siteId)} data-testid="delete-site">
                          Delete
                        </Button>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
